import React, {useEffect, useState} from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {Link, useNavigate, useParams} from "react-router-dom";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

import BoxContent from "shared/ui/BoxContent";
import PageTitle from "shared/ui/PageTitle";
import {APIGetTeacher} from "features/users/api/teacher";
import Loading from "shared/ui/Loading";
import {useAuth} from "hooks/useAuth";
import {APIUpdateTeacher, APIPostTeacher} from "features/users/api/teacher";
import FormField from "shared/form/FormField";
import ErrorsList from "shared/ui/ErrorsList";
import {useLocation} from "react-router-dom";
import {APIGetSubjects} from "features/users/api/subjects";
import {arrayEquals, getRomanNumber, niceUserName} from "utils/functions";
import FormLabelBox from "shared/ui/FormLabelBox";
import FormFieldBox from "shared/ui/FormFieldBox";
import GenericButton from "shared/ui/GenericButton";
import CustomButton from "../../../shared/ui/CustomButton";
import {useServerErrors} from "../../../hooks/useServerErrors";

interface IFormTeacherEdit {
    last_name: string;
    first_name: string;
    email: string;
    subjects: any[];
    classrooms: any[];
    status: number;
    password?: string;
    confirm_password?: string;
}

interface ISubjects {
    value: string;
    label: string;
}

const schema = yup
    .object({})
    .required();

const Teacher = () => {
    const {id = "", action} = useParams();
    const {update} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const methods = useForm<IFormTeacherEdit>({
        resolver: yupResolver(schema),
        shouldUnregister: true,
    });
    const [loadingSubjects, setLoadingSubjects] = useState<boolean>(true);
    const [loadingTeacher, setLoadingTeacher] = useState<boolean>(true);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [teacher, setTeacher] = useState<IFormTeacherEdit>({
        last_name: "",
        first_name: "",
        email: "",
        status: 1,
        subjects: [],
        classrooms: [],
    });
    const [teacherName, setTeacherName] = useState<string>("");
    const [subjects, setSubjects] = useState<ISubjects[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const { handleServerErrors } = useServerErrors<IFormTeacherEdit>(methods.setError);

    useEffect(() => {
        setLoadingTeacher(true);
        setLoadingSubjects(true);

        APIGetSubjects()
            .then((response) => {
                if (response.data.success) {
                    // console.log("getSubjects Ok", response);
                    setSubjects(
                        response.data.data.items.map((item: { name: string; id: string }) => {
                            return {
                                label: item.name,
                                value: item.id,
                                ...item,
                            };
                        }),
                    );
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch((err) => {
                // console.log("getSubjects ERR", err);
                update("error", {show: true, code: "A001"});
            })
            .finally(() => {
                setLoadingSubjects(false);
            })

        if (id !== "") {
            APIGetTeacher({id: id})
                .then((response) => {
                    if (response.data.success) {
                        // console.log("getTeacher OK", response);
                        if (response.data.data !== null) {
                            setTeacher(response.data.data.item);
                            setTeacherName(niceUserName(response.data.data.item.first_name, response.data.data.item.last_name, response.data.data.item.email));
                        } else navigate("/");
                    } else {
                        update("error", {show: true, code: "A002"});
                    }
                })
                .catch(() => update("error", {show: true, code: "A001"}))
                .finally(() =>  setLoadingTeacher(false))
        } else {
            let locationArray = location.pathname.split("/");
            if (locationArray[locationArray.length - 1] === "adauga") {
                setEditMode(true);
                setLoadingTeacher(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (id !== "") {
            methods.clearErrors();
            setErrorMessage("");
            setEditMode(action === "editare");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, id]);

    const handleSubmitError = (err: any) => {
        if (err.response.status === 400) {
            handleServerErrors(err.response.data.data, {});
            update("loading", {show: false});
        } else {
            update("error", {show: true, code: "A001"});
        }
    };

    const onSubmit: SubmitHandler<IFormTeacherEdit> = (data) => {
        let teacherSubjects: number[] = [];
        let oldTeacherSubjects: number[] = [];

        data.subjects && (teacherSubjects = data.subjects.map(({value}) => value));
        teacher.subjects && (oldTeacherSubjects = teacher.subjects.map(({id}: any) => id));

        if (
            data.last_name !== teacher.last_name ||
            data.first_name !== teacher.first_name ||
            data.email !== teacher.email ||
            !arrayEquals(teacherSubjects, oldTeacherSubjects) ||
            data.password !== undefined || data.confirm_password !== undefined
        ) {
            methods.clearErrors();
            setErrorMessage("");
            update("loading", {show: true, modal: true});
            if (id !== "") {
                APIUpdateTeacher(id, {
                    last_name: data.last_name,
                    first_name: data.first_name,
                    email: data.email,
                    subjects: teacherSubjects,
                    status: teacher.status,
                    password: data.password,
                    confirm_password: data.confirm_password,
                })
                    .then((response) => {
                        if (response.data.success) {
                            // console.log("updateTeacher OK", response);
                            // logic after update
                            setTeacher(prevState => ({...prevState, last_name: data.last_name, first_name: data.first_name, email: data.email, subjects: data.subjects}));
                            setEditMode(false);
                            navigate(-1);
                        } else {
                            update("error", {show: true, code: "A002"});
                        }
                    })
                    .catch(handleSubmitError)
                    .finally(() =>  update("loading", {show: false}))
            } else {
                APIPostTeacher({
                    last_name: data.last_name,
                    first_name: data.first_name,
                    email: data.email,
                    subjects: teacherSubjects,
                    status: 1,
                    password: data.password,
                    confirm_password: data.confirm_password,
                })
                    .then((response) => {
                        if (response.data.success) {
                            // console.log("postTeacher OK", response);
                            setTeacher(data);
                            setEditMode(false);
                            navigate(`/profesori/${response.data.data.item.id}`, {replace: true});
                        } else {
                            update("error", {show: true, code: "A002"});
                        }
                    })
                    .catch(handleSubmitError)
                    .finally(() =>  update("loading", {show: false}))
            }
        } else {
            setErrorMessage("Nu ați modificat niciun câmp.");
        }
    };

    return (
        <Box component="main">
            {(loadingTeacher || loadingSubjects) ? (
                <Loading show={true}/>
            ) : (
                <>
                    <PageTitle
                        back={true}
                        actions={
                            editMode ? (
                                <GenericButton type="close" onClick={() => {navigate(-1);}}/>
                            ) : (
                                <GenericButton type="edit" onClick={() => {navigate(`/profesori/${id}/editare`);}}/>
                            )
                        }
                    >
                        {id !== "" ? (
                            <>Profesor <strong style={{fontWeight: 400}}>{teacherName}</strong></>
                        ) : (
                            <>Adaugă profesor</>
                        )}
                    </PageTitle>

                    <BoxContent>
                        <Box width="100%">
                            <FormProvider {...methods}>
                                <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                                    <Grid container spacing={1.5} alignItems="center">

                                        <FormLabelBox title="Nume:"/>
                                        <FormFieldBox>
                                            {!editMode ? (
                                                teacher.last_name
                                            ) : (
                                                <FormField
                                                    name="last_name"
                                                    type="text"
                                                    defaultValue={teacher.last_name}
                                                    label="Nume"
                                                    autoFocus={true}
                                                    errorMessage={methods.formState.errors.last_name?.message}
                                                    error={!!methods.formState.errors.last_name?.message}
                                                />
                                            )}
                                        </FormFieldBox>

                                        <FormLabelBox title="Prenume:"/>

                                        <FormFieldBox>
                                            {!editMode ? (
                                                teacher.first_name
                                            ) : (
                                                <FormField
                                                    name="first_name"
                                                    type="text"
                                                    defaultValue={teacher.first_name}
                                                    label="Prenume"
                                                    errorMessage={methods.formState.errors.first_name?.message}
                                                    error={!!methods.formState.errors.first_name?.message}
                                                />
                                            )}
                                        </FormFieldBox>

                                        <FormLabelBox title="E-mail:"/>

                                        <FormFieldBox>
                                            {!editMode ? (
                                                teacher.email
                                            ) : (
                                                <FormField
                                                    name="email"
                                                    type="text"
                                                    defaultValue={teacher.email}
                                                    label="E-mail"
                                                    errorMessage={methods.formState.errors.email?.message}
                                                    error={!!methods.formState.errors.email?.message}
                                                    inputProps={{
                                                        autoCapitalize: 'none',
                                                        autoComplete: 'new-email',
                                                    }}
                                                />
                                            )}
                                        </FormFieldBox>

                                        <FormLabelBox title="Materii:"/>

                                        <FormFieldBox>
                                            {!editMode ? (
                                                teacher.subjects ? (
                                                    <>
                                                        {teacher.subjects.map((subject: any, index: number) => (
                                                            <Box key={index}>{subject.name}</Box>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <Box>Nimic selectat</Box>
                                                )
                                            ) : (
                                                <FormField
                                                    name="subjects"
                                                    type="autocomplete"
                                                    defaultValue={
                                                        teacher.subjects &&
                                                        teacher.subjects.map((item: { name: string; id: string }) => {
                                                            return {
                                                                label: item.name,
                                                                value: item.id,
                                                                ...item,
                                                            };
                                                        })
                                                    }
                                                    label="Alege materiile"
                                                    multiple={true}
                                                    options={subjects}
                                                    errorMessage={methods.formState.errors.subjects?.message}
                                                    error={!!methods.formState.errors.subjects?.message}
                                                />
                                            )}
                                        </FormFieldBox>


                                        {!editMode && (
                                            <>
                                                <FormLabelBox title="Clasă:"/>
                                                <FormFieldBox>
                                                    {teacher.classrooms ? (
                                                        <>
                                                            {teacher.classrooms.map((classroom: any, index: number) => (
                                                                <Box key={index}>
                                                                    <Link
                                                                        to={`/clase/${classroom.id}`}>({getRomanNumber(classroom.grade)}) {classroom.name}</Link>
                                                                </Box>
                                                            ))}
                                                        </>
                                                    ) : (
                                                        <Box>Nimic selectat</Box>
                                                    )}
                                                </FormFieldBox>
                                            </>
                                        )}

                                        {editMode && (
                                            <>
                                                <FormLabelBox title="Parola:" />
                                                <FormFieldBox>
                                                    <FormField
                                                        name="password"
                                                        type="password"
                                                        label="Parolă"
                                                        autoComplete="new-password"
                                                        error={!!methods.formState.errors.password?.message}
                                                        errorMessage={methods.formState.errors.password?.message}
                                                    />
                                                </FormFieldBox>

                                                <FormLabelBox title="Confirmă parola:" />
                                                <FormFieldBox>
                                                    <FormField
                                                        name="confirm_password"
                                                        type="password"
                                                        label="Confirmă parola"
                                                        autoComplete="new-password"
                                                        error={!!methods.formState.errors.confirm_password?.message}
                                                        errorMessage={methods.formState.errors.confirm_password?.message}
                                                    />
                                                </FormFieldBox>
                                            </>
                                        )}

                                        {editMode && (
                                            <>
                                                <FormLabelBox/>
                                                <FormFieldBox>
                                                    <CustomButton submit buttonType="textIcon" bgcolor="dark">
                                                        {id !== "" ? "SALVEAZĂ DATELE" : "ADAUGĂ PROFESOR"}
                                                    </CustomButton>
                                                    <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                                                </FormFieldBox>
                                            </>
                                        )}
                                    </Grid>
                                </form>
                            </FormProvider>
                        </Box>
                    </BoxContent>
                </>
            )}
        </Box>
    );
};

export default Teacher;
